import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import {
  ExportType,
  MetricKey,
  MetricPayload,
  MetricView,
  MetricsFacade,
  getMetricPayload,
} from '@app/shared/+state/metrics';
import { MetricColour } from '@app/shared/components/metric-widget-mono/metric-widget.component.interface';
import { Filter } from '@app/shared/interfaces/lib/filter.interface';
import { LoadingState } from '@app/shared/interfaces/lib/loading.interface';
import { User } from '@app/shared/interfaces/lib/user.interface';
import { TableData } from '@app/ui/table/table.component.interface';
import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  ExportMetricsDialogComponent,
  ExportMetricsDialogPayload,
} from '../export-metrics-dialog/export-metrics-dialog.component';
import { ListViewDialogComponent } from '../list-view-dialog/list-view-dialog.component';

@Component({
  selector: `hospitable-metrics-tasks`,
  templateUrl: './tasks.component.html',
  styleUrls: ['./tasks.component.scss', './tasks.component.mobile.scss'],
})
export class TasksComponent implements OnInit, OnDestroy {
  @Input() user: User | null;

  LoadingState = LoadingState;
  MetricColour = MetricColour;

  filtersLoadingState$: Observable<LoadingState | null>;
  filters$: Observable<Filter[] | null>;
  appliedFilters$: Observable<Filter[] | null>;

  detailsLoadingState$: Observable<LoadingState | null>;
  details$: Observable<TableData | null>;

  count: number | null;
  countSubscription$: Subscription;

  views$: Observable<MetricView[] | null>;
  activeView$: Observable<MetricView | null>;

  MetricKey = MetricKey;

  constructor(
    private metricsFacade: MetricsFacade,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.filtersLoadingState$ = this.metricsFacade.tasksFiltersLoadingState$;
    this.detailsLoadingState$ = this.metricsFacade.tasksDetailsLoadingState$;
    this.filters$ = this.metricsFacade.tasksFilters$;
    this.appliedFilters$ = this.metricsFacade.tasksAppliedFilters$;
    this.details$ = this.metricsFacade.tasksDetails$;
    this.countSubscription$ = this.metricsFacade.tasksCount$.subscribe((count) => (this.count = count));
    this.activeView$ = this.metricsFacade.activeTasksView$;
    this.views$ = this.metricsFacade.tasksViews$;
    this.metricsFacade.loadTasksFilters();
    this.metricsFacade.loadTasksViews();
  }

  ngOnDestroy() {
    if (this.countSubscription$ && this.countSubscription$.unsubscribe) {
      this.countSubscription$.unsubscribe();
    }
  }

  onAddFilter(filter: Filter) {
    this.metricsFacade.addTasksFilter(filter);
  }

  onApplyFilter(filter: Filter) {
    this.metricsFacade.applyTasksFilter(filter);
  }

  onRemoveFilter(filter: Filter) {
    this.metricsFacade.removeTasksFilter(filter);
  }

  onClearAllFilters() {
    console.log('onClearAllFilters=> ');
  }

  onDetailsPageChange(page: number, appliedFilters: Filter[] | null) {
    const payload: MetricPayload = {
      one_file_per_property: false,
      filters: {} as MetricPayload['filters'],
    };

    if (appliedFilters) {
      for (let i = 0; i < appliedFilters.length; i++) {
        const filter = appliedFilters[i];
        payload.filters[filter.key] = filter.initial_payload;
      }
    }

    this.metricsFacade.loadTasksDetails(payload, page);
  }

  export(appliedFilters: Filter[] | null) {
    const payload = getMetricPayload(appliedFilters);
    if (this.user) {
      const dialogRef = this.dialog.open(ExportMetricsDialogComponent, {
        width: '720px',
        maxHeight: '90vh',
        data: {
          email: this.user.email,
          rowCount: this.metricsFacade.tasksCount$,
          shouldHideIncludeKPIs: true,
        },
      });

      let exportType: ExportType;

      this.metricsFacade.loadTasksCount(false);

      const dialogSubscription$ = dialogRef.componentInstance.exportTypeUpdated.subscribe(
        (exportTypeSelected: ExportType) => {
          if (exportTypeSelected) {
            exportType = exportTypeSelected;
            this.metricsFacade.loadTasksCount(exportType === ExportType.Multiple ? true : false);
          }
        }
      );

      const dialogClosedSubscription$ = dialogRef.componentInstance.dialogClosed.subscribe(
        (dialogPayload: ExportMetricsDialogPayload) => {
          if (dialogPayload) {
            const { exportType, includeKPIs } = dialogPayload;

            this.metricsFacade.exportTasksMetrics({
              ...payload,
              ...{
                one_file_per_property: exportType === ExportType.Multiple ? true : false,
                include_kpis: includeKPIs,
              },
            });
          }
        }
      );

      dialogRef.afterClosed().subscribe(() => {
        dialogSubscription$ && dialogSubscription$.unsubscribe ? dialogSubscription$.unsubscribe() : null;
        dialogClosedSubscription$ && dialogClosedSubscription$.unsubscribe
          ? dialogClosedSubscription$.unsubscribe()
          : null;
      });
    }
  }

  openListViewDialog(views: MetricView[]) {
    this.activeView$.pipe(take(1)).subscribe((view) => {
      this.dialog.open(ListViewDialogComponent, {
        width: '720px',
        maxHeight: '90vh',
        data: {
          metricKey: MetricKey.Tasks,
          views,
          activeView: view,
        },
      });
    });
  }

  clearActiveView(event: any) {
    event.stopPropagation();
    this.metricsFacade.clearActiveTasksView();
  }
}
